import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import { CrossCircleIcon } from "../../components/Icons";
import { S3ProviderListOutputItem } from "@aws-amplify/storage";

type ModalProps = {
    showModal: boolean;
    setShowModal: (bool: boolean) => void;
    selectedFiles: S3ProviderListOutputItem[];
    handleDelete: (files: S3ProviderListOutputItem[]) => void;
    isProcessing: boolean;
    errorMessage: string;
};

/**
 * S3ファイル複数削除確認モーダル
 *
 * @param ModalProps
 * @returns JSX.Element
 */
const S3FilesDeleteConfirmModal: React.FC<ModalProps> = ({
    showModal,
    setShowModal,
    selectedFiles,
    handleDelete,
    isProcessing,
    errorMessage,
}) => {
    const { t } = useTranslation();

    return (
        <Modal
            className="fixed left-1/2 top-1/2 w-10/12 max-w-md -translate-x-1/2 -translate-y-1/2 transform whitespace-pre-wrap break-words rounded-lg border bg-white p-4 shadow-md"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50"
            isOpen={showModal}
        >
            <div className="m-0 px-4 py-2 sm:m-2">
                <div className="text-lg font-bold">{t("S3FilesDeleteConfirmModal.confirmtDeletefiles")}</div>
                <hr className="my-2 border-t-2" />

                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center py-2">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}

                {/* 選択されたファイル一覧 */}
                <div className="max-h-60 overflow-y-auto text-gray-900">
                    {selectedFiles.slice(0, 3).map((file, index) => (
                        <div key={index} className="flex justify-center py-1">
                            <span>{file.key?.split("/").slice(2).join("/")}</span>
                        </div>
                    ))}
                    {/* 4つ以上の要素がある場合は「... その他(n)ファイル」を表示 */}
                    {selectedFiles.length > 3 && (
                        <div className="flex justify-center py-1 text-gray-500">
                            <span>{t("S3FilesDeleteConfirmModal.text_others")} ({selectedFiles.length - 3}) {t("S3FilesDeleteConfirmModal.text_files")}</span>
                        </div>
                    )}
                </div>

                {/* キャンセル/削除ボタン */}
                <div className="mt-4 flex items-center justify-evenly">
                    <button className="btn-primary w-1/3" onClick={() => setShowModal(false)}>
                        {t("CommonLabel.cancel")}
                    </button>
                    <button
                        onClick={() => handleDelete(selectedFiles)}
                        className={`w-1/3 rounded-lg py-2.5 text-center font-medium text-gray-50 ${
                            isProcessing
                                ? "bg-red-900"
                                : "bg-red-600 hover:bg-red-800 active:bg-red-900 active:font-bold"
                        }`}
                        disabled={isProcessing}
                    >
                        {isProcessing ? (
                            <div className="flex items-center justify-center">
                                <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                <label>Processing...</label>
                            </div>
                        ) : (
                            t("CommonLabel.delete")
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default S3FilesDeleteConfirmModal;